import classNames from "classnames";
import { ReactNode, useEffect, useState } from "react";
import { Input } from "reactstrap";
import { BaseVariableProps, EmailVariables } from "../EmailBuilderTypes";
import IconButton from "../../../../elements/IconButton";
import { SelectPicker } from "rsuite";

interface Props extends BaseVariableProps {
  children: ReactNode;
  hideInput?: string;
  expandAll: boolean;
}

const VariableCollapse = ({
  variable,
  children,
  setVariables,
  index,
  hideInput,
  customVariables,
  expandAll,
}: Props) => {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setOpen(expandAll);
  }, [expandAll]);

  return (
    <div className="bg-superlight rounded my-1">
      <div className="d-flex justify-content-between ">
        <div
          className="d-flex align-items-center"
          style={{ minWidth: 200, width: "100%", maxWidth: 350 }}
        >
          <i
            className={classNames("cursor-pointer px-2 pt-1", {
              "mdi mdi-chevron-right": !open,
              "mdi mdi-chevron-down": open,
            })}
            onClick={() => setOpen((prev) => !prev)}
            style={{ fontSize: 28 }}
          />
          {hideInput ? (
            <span
              className="text-sm font-weight-500 no-select cursor-pointer"
              onClick={() => setOpen((prev) => !prev)}
            >
              {hideInput}
            </span>
          ) : (
            <>
              {customVariables ? (
                <VariablePicker
                  vars={customVariables}
                  variable={variable}
                  updateVariableName={updateVariableName}
                />
              ) : (
                <VariableInput
                  variable={variable}
                  updateVariableName={updateVariableName}
                />
              )}
            </>
          )}
        </div>
        <div className="d-flex align-items-center pr-2">
          <span className="text-uppercase font-weight-bold pr-3 pt-1">
            {variable.Type.replace(/([a-z])([A-Z])/g, "$1 $2")}
          </span>
          <IconButton
            icon="close"
            tooltipText="Remove Variable"
            color="danger"
            onClick={handleRemoveVariable}
          />
        </div>
      </div>
      {open && children}
    </div>
  );

  function handleRemoveVariable() {
    setVariables((prev) => prev.filter((_, i) => i !== index));
  }

  function updateVariableName(newName: string) {
    setVariables((prev) => {
      return prev.map((variable, i) => {
        if (i !== index) return variable;

        return { ...variable, Variable: newName };
      });
    });
  }
};

function VariableInput({
  variable,
  updateVariableName,
}: {
  variable: EmailVariables;
  updateVariableName: (name: string) => void;
}) {
  if (variable.Type === "SearchByEmail") return null;

  return (
    <Input
      placeholder="Variable Name"
      bsSize="sm"
      onChange={(e) => updateVariableName(e.target.value)}
      value={variable.Variable}
    />
  );
}

export function VariablePicker({
  vars,
  variable,
  updateVariableName,
}: {
  vars: string[];
  variable:
    | EmailVariables
    | { FieldAPIName: string; Variable: string; Type: "Value" };
  updateVariableName: (name: string) => void;
}) {
  if (variable.Type === "SearchByEmail") return null;

  return (
    <SelectPicker
      style={{ width: 200 }}
      placeholder="Select Variable"
      size="sm"
      data={vars.map((str) => ({
        label: str,
        value: str,
      }))}
      value={variable.Variable}
      onChange={(newName: string | null) => {
        updateVariableName(newName || "");
      }}
      cleanable={false}
      searchable={false}
    />
  );
}

export default VariableCollapse;
